import React, { useState } from "react";
import { FaCheck } from "react-icons/fa";
import { pricingPlans, BILLING_PERIODS } from "../data/pricingData";

export default function PricingSection({ onBookDemo }) {
  const [billingPeriod, setBillingPeriod] = useState(BILLING_PERIODS.ANNUAL);

  const getPrice = (plan) =>
    billingPeriod === BILLING_PERIODS.ANNUAL
      ? { total: plan.annual, perMonth: plan.perMonthAnnual, discount: plan.annualDiscount, periodLabel: "Billed annually" }
      : { total: plan.seasonal, perMonth: plan.perMonthSeasonal, discount: plan.seasonalDiscount, periodLabel: "6 months (Apr–Sep)" };

  const getFeatures = (plan) => [
    { label: "Teams", value: plan.teams },
    { label: "Posts", value: plan.posts },
    { label: "Users", value: plan.users },
    { label: "Support", value: plan.support },
    { label: "Custom Designs (per content type)", value: plan.customDesigns },
    { label: "Best for", value: plan.bestFor },
  ];

  return (
    <section
      className="py-12 md:py-20 bg-white"
      aria-labelledby="pricing-heading"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header - Templated.io style */}
        <div className="text-center mb-8 md:mb-12">
          <h2
            id="pricing-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-snaptrix-gray-800 mb-3"
          >
            Pick your plan. Grow your club.
          </h2>
          <p className="text-base md:text-lg text-snaptrix-gray-600 max-w-xl mx-auto mb-6">
            Simple, transparent pricing. No hidden fees. Choose the plan that fits your club.
          </p>

          {/* Billing toggle */}
          <div
            className="inline-flex flex-wrap justify-center items-center gap-2 sm:gap-3 p-1.5 sm:p-2 bg-snaptrix-gray-100 rounded-xl"
            role="group"
            aria-label="Billing period"
          >
            <button
              type="button"
              onClick={() => setBillingPeriod(BILLING_PERIODS.ANNUAL)}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-snaptrix-blue-500 focus:ring-offset-2 ${
                billingPeriod === BILLING_PERIODS.ANNUAL
                  ? "bg-white text-snaptrix-gray-800 shadow-sm"
                  : "text-snaptrix-gray-600 hover:text-snaptrix-gray-800"
              }`}
              aria-pressed={billingPeriod === BILLING_PERIODS.ANNUAL}
            >
              Billed annually
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod(BILLING_PERIODS.SEASONAL)}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-snaptrix-blue-500 focus:ring-offset-2 ${
                billingPeriod === BILLING_PERIODS.SEASONAL
                  ? "bg-white text-snaptrix-gray-800 shadow-sm"
                  : "text-snaptrix-gray-600 hover:text-snaptrix-gray-800"
              }`}
              aria-pressed={billingPeriod === BILLING_PERIODS.SEASONAL}
            >
              Seasonal
            </button>
            {billingPeriod === BILLING_PERIODS.ANNUAL && (
              <span className="px-2.5 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full whitespace-nowrap">
                Save 33%
              </span>
            )}
          </div>
          <p className="mt-3 text-sm text-snaptrix-gray-500">
            Seasonal = April–September (6 months), off-season no charge.
          </p>
        </div>

        {/* Plan cards - responsive grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {pricingPlans.map((plan) => {
            const price = getPrice(plan);
            const features = getFeatures(plan);
            const isPopular = plan.mostPopular;

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-2xl border-2 bg-white p-6 sm:p-8 transition-all ${
                  isPopular
                    ? "border-snaptrix-blue-600 shadow-lg shadow-snaptrix-blue-100 md:shadow-xl md:shadow-snaptrix-blue-100/50"
                    : "border-snaptrix-gray-200 hover:border-snaptrix-gray-300 hover:shadow-md"
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-block px-3 py-1 text-xs font-semibold bg-snaptrix-blue-600 text-white rounded-full">
                      Most popular
                    </span>
                  </div>
                )}

                {/* Plan name */}
                <h3 className="text-xl font-bold text-snaptrix-gray-800 mb-1">
                  {plan.name}
                </h3>

                {/* Price block - Templated style */}
                <div className="mt-4 mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl md:text-4xl font-bold text-snaptrix-gray-900">
                      {price.perMonth}
                    </span>
                    <span className="text-snaptrix-gray-500 text-sm">
                      / month
                    </span>
                  </div>
                  <p className="text-xs text-snaptrix-gray-500 mt-0.5">
                    {price.periodLabel}
                  </p>
                  {price.discount && (
                    <span
                      className={`inline-block mt-2 px-2.5 py-1 text-xs font-medium rounded-full ${
                        billingPeriod === BILLING_PERIODS.ANNUAL
                          ? "bg-green-100 text-green-800"
                          : "bg-snaptrix-gray-100 text-snaptrix-gray-600 border border-snaptrix-gray-200"
                      }`}
                    >
                      {price.discount}
                    </span>
                  )}
                </div>

                {/* Features list */}
                <ul className="flex-1 space-y-3 text-sm text-snaptrix-gray-600">
                  {features.map(({ label, value }) => (
                    <li key={label} className="flex gap-3">
                      <FaCheck
                        className="mt-0.5 shrink-0 text-green-600"
                        aria-hidden
                      />
                      <span>
                        <strong className="text-snaptrix-gray-700">{label}:</strong>{" "}
                        {value}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  type="button"
                  onClick={onBookDemo}
                  className={`mt-6 w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-snaptrix-blue-500 ${
                    isPopular
                      ? "bg-snaptrix-blue-600 text-white hover:bg-snaptrix-blue-700 shadow-md"
                      : "bg-snaptrix-gray-100 text-snaptrix-gray-800 hover:bg-snaptrix-gray-200 border border-snaptrix-gray-200"
                  }`}
                  aria-label={`Get started – ${plan.name}`}
                >
                  Get started
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
